import { PATH } from '@/router/PATH';

/** Tab identity — also the `Tabs` selection key and the testid discriminator. */
export type AnalyticsTabId = 'financial-position' | 'reporting';

export type AnalyticsTab = {
    id: AnalyticsTabId;
    /** Absolute route this tab owns; selecting the tab navigates here. */
    path: string;
    labelKey: string;
    /**
     * The tab is offered but has no content behind it yet (BOS-170).
     *
     * It stays in the bar on purpose — the shell's shape is settled around two
     * tabs, and a tab that appears later moves everything beside it. What it
     * gains is a chip saying so, because the complaint was never that the tab
     * exists: it is that clicking a plain tab and landing on "nothing here yet"
     * reads as a page that failed to load. Said in advance, it reads as a
     * roadmap.
     */
    comingSoon?: boolean;
};

/**
 * The Analytics tabs, in display order. The first is the default: the bare
 * `/analytics` renders it through an index route, so the tab bar reads the same
 * whether the URL carries the `financial-position` segment or not.
 */
export const ANALYTICS_TABS: AnalyticsTab[] = [
    {
        id: 'financial-position',
        path: PATH.ANALYTICS.FINANCIAL_POSITION,
        labelKey: 'analytics.tabs.financialPosition',
    },
    {
        id: 'reporting',
        path: PATH.ANALYTICS.REPORTING,
        labelKey: 'analytics.tabs.reporting',
        comingSoon: true,
    },
];

export const DEFAULT_ANALYTICS_TAB_ID: AnalyticsTabId = 'financial-position';

/**
 * Which tab the current URL selects. The bare shell path and anything
 * unrecognised fall back to the default tab, so the bar is never rendered with
 * nothing selected.
 */
export const resolveAnalyticsTabId = (pathname: string): AnalyticsTabId => {
    const matched = ANALYTICS_TABS.find(
        (tab) => pathname === tab.path || pathname.startsWith(`${tab.path}/`),
    );

    return matched?.id ?? DEFAULT_ANALYTICS_TAB_ID;
};

/** Route target for a tab id; unknown ids land on the default tab's route. */
export const resolveAnalyticsTabPath = (id: string): string => {
    const matched = ANALYTICS_TABS.find((tab) => tab.id === id);

    return matched?.path ?? PATH.ANALYTICS.FINANCIAL_POSITION;
};
